import React, { useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatINR, formatArea } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, login } from "@/lib/auth";
import SEO from "@/components/SEO";
import { CheckCircle2, Upload, X, ImagePlus, Video, Users, Award, BarChart3, Briefcase, TrendingUp, Building2, Eye, ShieldCheck, Star, MapPin, BedDouble, Bath, Maximize2, Lock } from "lucide-react";

const INPUT_CLS = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm rounded-none transition-colors";

const EMPTY_FORM = {
  title: "", property_type: "residential", listing_type: "sale",
  city: "Rewa", locality: "", address: "", price: "", area_value: "", area_unit: "sqft",
  bedrooms: 0, bathrooms: 0, road_width_ft: "", legal_status: "", description: "",
  images: [], video_url: "", map_link: "",
  gated_society: null, gas_pipeline: null, electricity_connection: null, water_availability: null, age_of_property_years: "",
  irrigated: null, irrigation_type: "", direct_road_access: null,
  premium_justification: "",
  // Rent-specific optional pricing (also allowed for sale). Each has a type toggle + a free-form value string.
  brokerage_type: "value", brokerage_value: "",
  security_deposit_type: "value", security_deposit_value: "",
};

export default function SellProperty() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isBrokerView = params.get("ref") === "broker";
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const input = INPUT_CLS;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!user) { toast.error("Sign in to upload images"); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!["image/jpeg","image/png","image/webp"].includes(f.type)) { toast.error(`${f.name}: only JPG/PNG/WebP allowed`); continue; }
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      }
      setForm((s) => ({ ...s, images: [...s.images, ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length>1?"s":""} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
  };

  const handleVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error("Sign in to upload video"); return; }
    if (!["video/mp4","video/quicktime","video/webm","video/x-m4v"].includes(f.type)) {
      toast.error("Only MP4/MOV/WebM allowed"); return;
    }
    if (f.size > 100 * 1024 * 1024) { toast.error("Max 100MB video"); return; }
    setUploadingVideo(true); setVideoProgress(0);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/upload/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setVideoProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setForm((s) => ({ ...s, video_url: url }));
      toast.success("Video uploaded");
    } catch {
      toast.error("Video upload failed");
    } finally {
      setUploadingVideo(false); setVideoProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        area_value: parseFloat(form.area_value),
        bedrooms: parseInt(form.bedrooms || 0),
        bathrooms: parseInt(form.bathrooms || 0),
        road_width_ft: form.road_width_ft ? parseFloat(form.road_width_ft) : null,
        age_of_property_years: form.age_of_property_years !== "" ? parseInt(form.age_of_property_years) : null,
        images: form.images.filter(Boolean),
        nearby: [],
      };
      // Strip null/empty conditional fields
      if (form.property_type !== "residential") {
        delete payload.gated_society; delete payload.gas_pipeline;
        delete payload.electricity_connection; delete payload.water_availability;
        delete payload.age_of_property_years;
      }
      if (form.property_type !== "agricultural") {
        delete payload.irrigated; delete payload.irrigation_type; delete payload.direct_road_access;
      }
      if (form.irrigated !== true) delete payload.irrigation_type;
      if (!payload.premium_justification) delete payload.premium_justification;
      // Strip empty brokerage / security_deposit — only send when the seller actually filled them
      if (!payload.brokerage_value || !String(payload.brokerage_value).trim()) {
        delete payload.brokerage_value; delete payload.brokerage_type;
      }
      if (!payload.security_deposit_value || !String(payload.security_deposit_value).trim()) {
        delete payload.security_deposit_value; delete payload.security_deposit_type;
      }
      const r = await api.post("/properties", payload);
      setShowPreview(false);
      setSuccess({ slug: r.data.slug, isLive: r.data.status === "approved" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const validate = () => {
    if (!form.title || !form.locality || !form.price || !form.area_value || !form.description) {
      toast.error("Please fill mandatory fields"); return false;
    }
    if (form.images.filter(Boolean).length < 3) {
      toast.error("Please add at least 3 images"); return false;
    }
    return true;
  };

  const openPreview = () => { if (validate()) setShowPreview(true); };

  return (
    <div data-testid="sell-page">
      <SEO
        title={isBrokerView ? "For Brokers & Developers · List on Tharlo" : "List Your Property · Sell on Tharlo"}
        description="List properties on Tharlo — owners, brokers, developers welcome. Verified listings get premium qualified buyers."
      />
      {isBrokerView && <BrokerHero />}

      <div className="container-tharlo py-12">
        <div className="max-w-3xl">
        {success ? (
          <SuccessScreen success={success} onList={() => { setSuccess(null); setForm(EMPTY_FORM); }} />
        ) : (
          <>
        <div className="overline mb-3">Sell · List · Submit</div>
        <h1 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">List Your Property on Tharlo</h1>
        <p className="text-foreground/70 mt-4">All fields marked <span className="text-gold font-semibold">Public</span> will be visible to buyers. Fields marked <span className="inline-flex items-center gap-1 text-ink font-semibold"><Lock className="w-3 h-3" /> Set by Tharlo</span> are decided by our admin team after review.</p>

        {!user && (
          <div className="mt-6 bg-gold/10 border border-gold/30 p-4 text-sm flex items-center justify-between">
            <span>Sign in to upload images & publish your listing instantly.</span>
            <button onClick={login} className="text-gold font-semibold hover:underline">Sign in</button>
          </div>
        )}

        <TharloDecidesBanner />

        <form onSubmit={submit} className="mt-8 space-y-5">
          <BasicsSection form={form} setForm={setForm} />
          <LocationSection form={form} setForm={setForm} />
          <SpecsSection form={form} setForm={setForm} />

          {form.property_type === "residential" && <ResidentialAmenitiesSection form={form} setForm={setForm} />}

          {form.property_type === "agricultural" && <AgriculturalSection form={form} setForm={setForm} />}

          <Section title="Description">
            <textarea data-testid="sell-description" required className={input} rows={5} placeholder="Tell buyers about the property... *" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} />
          </Section>

          <Section title="Images" subtitle="Upload at least 3 images. JPG/PNG/WebP, max 8MB each.">
            <MediaSection
              form={form}
              setForm={setForm}
              user={user}
              fileInputRef={fileInputRef}
              videoInputRef={videoInputRef}
              uploading={uploading}
              uploadingVideo={uploadingVideo}
              videoProgress={videoProgress}
              onFiles={handleFiles}
              onVideoFile={handleVideoFile}
              onRemoveImage={removeImage}
            />
          </Section>

          <PremiumJustificationBlock form={form} setForm={setForm} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button type="button" onClick={openPreview} data-testid="sell-preview" className="btn-outline w-full">
              <Eye className="w-4 h-4" /> Preview How Buyers See It
            </button>
            <button type="submit" data-testid="sell-submit" disabled={submitting} className="btn-primary w-full">
              <Upload className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit & Publish"}
            </button>
          </div>

          <div className="text-xs text-muted-foreground flex items-center gap-2 pt-2">
            <CheckCircle2 className="w-4 h-4 text-gold" />
            {user ? "Once submitted, your listing goes live instantly. You can edit or delete anytime from My Listings." : "Guest submissions are reviewed within 48 hours. Sign in for instant publishing."}
          </div>
        </form>
        </>
        )}
      </div>
      </div>

      {showPreview && (
        <PreviewModal form={form} user={user} onClose={() => setShowPreview(false)} onConfirm={submit} submitting={submitting} />
      )}
    </div>
  );
}

/* ============ Form sections ============ */

function BasicsSection({ form, setForm }) {
  return (
    <Section title="Property Basics">
      <input data-testid="sell-title" required className={INPUT_CLS} placeholder="Property title *" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select data-testid="sell-type" className={INPUT_CLS} value={form.property_type} onChange={(e)=>setForm({...form, property_type:e.target.value})}>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
          <option value="plot">Plot</option>
          <option value="agricultural">Agricultural</option>
        </select>
        <select data-testid="sell-listing-type" className={INPUT_CLS} value={form.listing_type} onChange={(e)=>setForm({...form, listing_type:e.target.value})}>
          <option value="sale">For Sale</option>
          <option value="rent">For Rent</option>
        </select>
      </div>
    </Section>
  );
}

function LocationSection({ form, setForm }) {
  return (
    <Section title="Location">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input data-testid="sell-city" className={INPUT_CLS} placeholder="City *" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} />
        <input data-testid="sell-locality" className={INPUT_CLS} placeholder="Locality *" value={form.locality} onChange={(e)=>setForm({...form, locality:e.target.value})} />
      </div>
      <input data-testid="sell-address" className={INPUT_CLS} placeholder="Full address (optional)" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} />
      <input data-testid="sell-map" className={INPUT_CLS} placeholder="Google Maps link (optional)" value={form.map_link} onChange={(e)=>setForm({...form, map_link:e.target.value})} />
    </Section>
  );
}

const BROKERAGE_PLACEHOLDERS = {
  value: "e.g. 15000",
  percent: "e.g. 100 (means 1 month rent) or 15",
  text: "e.g. 15 days rent · negotiable",
};
const DEPOSIT_PLACEHOLDERS = {
  value: "e.g. 60000",
  percent: "e.g. 200 (2 months rent)",
  text: "e.g. 2 months rent · refundable",
};

function ChargeField({ label, typeKey, valueKey, placeholders, form, setForm, typeTestId, valueTestId }) {
  return (
    <FieldLabel label={label}>
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <select data-testid={typeTestId} className={INPUT_CLS} value={form[typeKey]} onChange={(e)=>setForm({...form, [typeKey]:e.target.value})}>
          <option value="value">₹ Value</option>
          <option value="percent">% of Rent</option>
          <option value="text">Text</option>
        </select>
        <input
          data-testid={valueTestId}
          className={INPUT_CLS}
          placeholder={placeholders[form[typeKey]] || placeholders.text}
          value={form[valueKey]}
          onChange={(e)=>setForm({...form, [valueKey]:e.target.value})}
        />
      </div>
    </FieldLabel>
  );
}

function SpecsSection({ form, setForm }) {
  return (
    <Section title="Specifications">
      <FieldLabel label="Price (₹) *">
        <input data-testid="sell-price" type="number" className={INPUT_CLS} placeholder="e.g. 5000000" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} />
      </FieldLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FieldLabel label="Area *">
          <input data-testid="sell-area" type="number" className={INPUT_CLS} placeholder="e.g. 1200" value={form.area_value} onChange={(e)=>setForm({...form, area_value:e.target.value})} />
        </FieldLabel>
        <FieldLabel label="Area Unit">
          <select data-testid="sell-area-unit" className={INPUT_CLS} value={form.area_unit} onChange={(e)=>setForm({...form, area_unit:e.target.value})}>
            <option value="sqft">sqft</option><option value="sqyd">sqyd</option>
            <option value="acre">acre</option><option value="bigha">bigha</option>
          </select>
        </FieldLabel>
        <FieldLabel label="Road Width (ft)">
          <input data-testid="sell-road" type="number" className={INPUT_CLS} placeholder="e.g. 30" value={form.road_width_ft} onChange={(e)=>setForm({...form, road_width_ft:e.target.value})} />
        </FieldLabel>
      </div>
      {form.property_type === "residential" && (
        <div className="grid grid-cols-2 gap-3">
          <FieldLabel label="Number of Bedrooms">
            <input data-testid="sell-beds" type="number" min="0" className={INPUT_CLS} placeholder="e.g. 3" value={form.bedrooms} onChange={(e)=>setForm({...form, bedrooms:e.target.value})} />
          </FieldLabel>
          <FieldLabel label="Number of Washrooms">
            <input data-testid="sell-baths" type="number" min="0" className={INPUT_CLS} placeholder="e.g. 2" value={form.bathrooms} onChange={(e)=>setForm({...form, bathrooms:e.target.value})} />
          </FieldLabel>
        </div>
      )}
      <FieldLabel label="Legal Status">
        <input data-testid="sell-legal" className={INPUT_CLS} placeholder="e.g. Clear Title / RERA Approved / Patta Verified" value={form.legal_status} onChange={(e)=>setForm({...form, legal_status:e.target.value})} />
      </FieldLabel>

      {form.listing_type === "rent" && (
        <div className="pt-4 border-t border-border space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-gold font-semibold">Rent Charges (optional)</div>
          <p className="text-[11px] text-muted-foreground -mt-2">If left empty, these won&apos;t be shown on the public page.</p>
          <ChargeField
            label="Brokerage"
            typeKey="brokerage_type"
            valueKey="brokerage_value"
            placeholders={BROKERAGE_PLACEHOLDERS}
            form={form}
            setForm={setForm}
            typeTestId="sell-brokerage-type"
            valueTestId="sell-brokerage-value"
          />
          <ChargeField
            label="Security Deposit"
            typeKey="security_deposit_type"
            valueKey="security_deposit_value"
            placeholders={DEPOSIT_PLACEHOLDERS}
            form={form}
            setForm={setForm}
            typeTestId="sell-deposit-type"
            valueTestId="sell-deposit-value"
          />
        </div>
      )}
    </Section>
  );
}

function ResidentialAmenitiesSection({ form, setForm }) {
  return (
    <Section title="Residential Amenities">
      <YesNo label="Gated Society" value={form.gated_society} onChange={(v)=>setForm({...form, gated_society:v})} testid="sell-gated" />
      <YesNo label="Gas Pipeline" value={form.gas_pipeline} onChange={(v)=>setForm({...form, gas_pipeline:v})} testid="sell-gas" />
      <YesNo label="Electricity Connection" value={form.electricity_connection} onChange={(v)=>setForm({...form, electricity_connection:v})} testid="sell-electricity" />
      <YesNo label="Water Availability" value={form.water_availability} onChange={(v)=>setForm({...form, water_availability:v})} testid="sell-water" />
      <FieldLabel label="Age of Property (years)">
        <input data-testid="sell-age" type="number" min="0" className={INPUT_CLS} placeholder="e.g. 5 (0 for new construction)" value={form.age_of_property_years} onChange={(e)=>setForm({...form, age_of_property_years:e.target.value})} />
      </FieldLabel>
    </Section>
  );
}

function AgriculturalSection({ form, setForm }) {
  return (
    <Section title="Agricultural Details">
      <YesNo label="Irrigated Land" value={form.irrigated} onChange={(v)=>setForm({...form, irrigated:v, irrigation_type: v ? form.irrigation_type : ""})} testid="sell-irrigated" />
      {form.irrigated === true && (
        <FieldLabel label="Type of Irrigation">
          <select data-testid="sell-irrigation-type" className={INPUT_CLS} value={form.irrigation_type} onChange={(e)=>setForm({...form, irrigation_type:e.target.value})}>
            <option value="">Select type</option>
            <option value="well">Well</option>
            <option value="canal">Canal</option>
            <option value="borewell">Borewell</option>
            <option value="other">Other</option>
          </select>
        </FieldLabel>
      )}
      <YesNo label="Direct Road Access" value={form.direct_road_access} onChange={(v)=>setForm({...form, direct_road_access:v})} testid="sell-direct-road" />
    </Section>
  );
}

function MediaSection({ form, setForm, user, fileInputRef, videoInputRef, uploading, uploadingVideo, videoProgress, onFiles, onVideoFile, onRemoveImage }) {
  let imageUploadLabel = user ? "Click to upload images" : "Sign in to upload";
  if (uploading) imageUploadLabel = "Uploading...";
  let videoUploadLabel = user ? "Or upload a video from your device" : "Sign in to upload video";
  if (uploadingVideo) videoUploadLabel = `Uploading video... ${videoProgress}%`;
  const isUploadedVideo = form.video_url.startsWith("http") && form.video_url.includes(process.env.REACT_APP_BACKEND_URL || "");

  return (
    <>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onFiles} className="hidden" data-testid="sell-image-input" />
      <button
        type="button"
        onClick={() => user ? fileInputRef.current?.click() : login()}
        disabled={uploading}
        data-testid="sell-upload-trigger"
        className="w-full border-2 border-dashed border-border hover:border-gold p-8 flex flex-col items-center gap-2 transition-colors disabled:opacity-50"
      >
        <ImagePlus className="w-8 h-8 text-gold" />
        <div className="font-semibold text-ink">{imageUploadLabel}</div>
        <div className="text-xs text-muted-foreground">JPG, PNG, WebP · Max 8MB · Multiple allowed</div>
      </button>

      {form.images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="sell-image-preview">
          {form.images.map((src, i) => (
            <div key={src} className="relative aspect-[4/3] group">
              <img src={src} alt="" className="w-full h-full object-cover border border-border" />
              <button type="button" onClick={()=>onRemoveImage(i)} data-testid={`remove-img-${i}`} className="absolute top-1 right-1 bg-ink text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
              {i === 0 && <span className="absolute bottom-1 left-1 bg-gold text-white text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wider">Cover</span>}
            </div>
          ))}
        </div>
      )}

      <input data-testid="sell-video" className={INPUT_CLS} placeholder="Video URL (YouTube/Vimeo, optional)" value={form.video_url} onChange={(e)=>setForm({...form, video_url:e.target.value})} />

      <div className="mt-2">
        <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" onChange={onVideoFile} className="hidden" data-testid="sell-video-input" />
        <button
          type="button"
          onClick={() => user ? videoInputRef.current?.click() : login()}
          disabled={uploadingVideo}
          data-testid="sell-video-upload-trigger"
          className="w-full border-2 border-dashed border-border hover:border-gold p-6 flex flex-col items-center gap-2 transition-colors disabled:opacity-50"
        >
          <Video className="w-7 h-7 text-gold" />
          <div className="font-semibold text-ink text-sm">{videoUploadLabel}</div>
          <div className="text-xs text-muted-foreground">MP4, MOV, WebM · Max 100MB</div>
          {uploadingVideo && (
            <div className="w-full bg-muted h-1 mt-2 overflow-hidden">
              <div className="bg-gold h-full transition-all" style={{ width: `${videoProgress}%` }} />
            </div>
          )}
        </button>
        {form.video_url && !uploadingVideo && (
          <div className="mt-2 flex items-center justify-between bg-white border border-border p-3 text-xs">
            <span className="truncate text-muted-foreground pr-3" data-testid="sell-video-preview">{isUploadedVideo ? "Uploaded video attached" : form.video_url}</span>
            <button type="button" onClick={() => setForm({...form, video_url:""})} data-testid="sell-video-remove" className="text-red-600 hover:underline">Remove</button>
          </div>
        )}
      </div>
    </>
  );
}

function PremiumJustificationBlock({ form, setForm }) {
  return (
    <div className="bg-ink text-white p-5 border border-ink" data-testid="premium-justification-block">
      <div className="flex items-start gap-3 mb-3">
        <Lock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-xl text-white">Why does your property deserve the Premium badge?</h3>
          <p className="text-xs text-white/70 mt-1">
            Strictly for internal use by the Tharlo team. Not shown to buyers. Based on this note + our internal quality checks, our admin decides whether to grant the Premium/Featured status.
          </p>
        </div>
      </div>
      <textarea
        data-testid="sell-premium-justification"
        className="w-full bg-white/95 text-ink border border-white/20 focus:border-gold outline-none px-4 py-3 text-sm rounded-none"
        rows={4}
        placeholder="e.g. Corner plot with 3-side road, exceptional connectivity, gated society amenities, recent flooring & interiors, RERA-registered developer, high rental yield locality..."
        value={form.premium_justification}
        onChange={(e)=>setForm({...form, premium_justification:e.target.value})}
        maxLength={2000}
      />
      <div className="text-[11px] text-white/60 text-right mt-1">{form.premium_justification.length}/2000</div>
    </div>
  );
}

function SuccessScreen({ success, onList }) {
  return (
    <div className="text-center py-12" data-testid="sell-success">
      <div className="w-20 h-20 bg-gold/10 border border-gold/40 mx-auto flex items-center justify-center mb-6">
        <CheckCircle2 className="w-10 h-10 text-gold" />
      </div>
      <h1 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">Property is live now.</h1>
      <p className="text-foreground/70 mt-4 max-w-lg mx-auto">
        Your listing is now visible to buyers on Tharlo. You can review, edit, mark it sold, or delete it anytime from <strong>My Listings</strong> in your seller dashboard.
      </p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/broker?s=properties" data-testid="success-my-listings" className="btn-primary">Go to My Listings</Link>
        <Link to={`/property/${success.slug}`} target="_blank" data-testid="success-view-live" className="btn-outline">View Live Listing</Link>
        <button onClick={onList} data-testid="success-list-another" className="btn-gold">List Another Property</button>
      </div>
    </div>
  );
}

function TharloDecidesBanner() {
  return (
    <div className="mt-6 bg-ink text-white p-5 border border-ink" data-testid="tharlo-decides-banner">
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-white">These are decided by Tharlo</div>
          <div className="text-xs text-white/70 mt-1 leading-relaxed">
            Verified badge · Premium/Featured status · Tharlo Fair Price Range · Connectivity Score (0-10) · Future Appreciation Potential · Legal Status verification. Our team assigns these after reviewing your listing to protect buyers and maintain platform trust.
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ form, user, onClose, onConfirm, submitting }) {
  const img = form.images?.[0] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80";
  const price = parseFloat(form.price || 0);
  const fairMin = price * 0.95;
  const fairMax = price * 1.05;
  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm overflow-y-auto" data-testid="preview-modal">
      <div className="min-h-full flex items-start justify-center p-4">
        <div className="bg-white w-full max-w-4xl border border-border my-4">
          <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between z-10">
            <div>
              <div className="overline">Preview — Buyer View</div>
              <h3 className="font-display text-xl text-ink mt-1">This is exactly how buyers will see your listing</h3>
            </div>
            <button onClick={onClose} data-testid="preview-close" className="p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-6">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="trust-badge">{form.property_type}</span>
              <span className="trust-badge">For {form.listing_type}</span>
              <span className="trust-badge bg-muted text-muted-foreground border-border" title="Assigned by Tharlo admin">
                <Lock className="w-3 h-3" /> Verified — pending Tharlo
              </span>
              <span className="trust-badge bg-muted text-muted-foreground border-border">
                <Lock className="w-3 h-3" /> Premium — pending Tharlo
              </span>
            </div>

            <h1 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">{form.title || "(Title)"}</h1>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-gold" /> {form.address || `${form.locality}, ${form.city}`}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="aspect-[16/10] bg-muted overflow-hidden">
                  <img src={img} alt={form.title} className="w-full h-full object-cover" />
                </div>
                {form.images.length > 1 && (
                  <div className="mt-2 grid grid-cols-5 gap-2">
                    {form.images.slice(0, 5).map((src) => (
                      <div key={src} className="aspect-[4/3] bg-muted overflow-hidden">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Specs */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 grid-spec">
                  <SpecTile label="Area" value={form.area_value ? formatArea(parseFloat(form.area_value), form.area_unit) : "-"} />
                  {parseInt(form.bedrooms||0) > 0 && <SpecTile label="Bedrooms" value={form.bedrooms} />}
                  {parseInt(form.bathrooms||0) > 0 && <SpecTile label="Washrooms" value={form.bathrooms} />}
                  {form.road_width_ft && <SpecTile label="Road Width" value={`${form.road_width_ft} ft`} />}
                </div>

                {/* Residential extras */}
                {form.property_type === "residential" && (form.gated_society !== null || form.gas_pipeline !== null || form.electricity_connection !== null || form.water_availability !== null || form.age_of_property_years !== "") && (
                  <div className="mt-6">
                    <h3 className="font-display text-lg text-ink mb-3">Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {form.gated_society !== null && <AmenityBadge label="Gated Society" yes={form.gated_society} />}
                      {form.gas_pipeline !== null && <AmenityBadge label="Gas Pipeline" yes={form.gas_pipeline} />}
                      {form.electricity_connection !== null && <AmenityBadge label="Electricity" yes={form.electricity_connection} />}
                      {form.water_availability !== null && <AmenityBadge label="Water Supply" yes={form.water_availability} />}
                      {form.age_of_property_years !== "" && <AmenityBadge label={`Age: ${form.age_of_property_years} yr${parseInt(form.age_of_property_years, 10) === 1?"":"s"}`} yes={true} />}
                    </div>
                  </div>
                )}

                {/* Agricultural extras */}
                {form.property_type === "agricultural" && (form.irrigated !== null || form.direct_road_access !== null) && (
                  <div className="mt-6">
                    <h3 className="font-display text-lg text-ink mb-3">Agricultural Details</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {form.irrigated !== null && <AmenityBadge label={form.irrigated ? `Irrigated (${form.irrigation_type || "type TBD"})` : "Not irrigated"} yes={form.irrigated} />}
                      {form.direct_road_access !== null && <AmenityBadge label="Direct Road Access" yes={form.direct_road_access} />}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <h2 className="font-display text-xl text-ink mb-2">About this Property</h2>
                  <p className="text-foreground/80 leading-relaxed text-sm whitespace-pre-wrap">{form.description || "(Description)"}</p>
                </div>

                {form.premium_justification && (
                  <div className="mt-6 bg-muted/50 border border-dashed border-muted-foreground/30 p-4">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-2">
                      <Lock className="w-3 h-3" /> Internal Note to Tharlo (Not shown to buyers)
                    </div>
                    <p className="text-xs text-foreground/60 italic">{form.premium_justification}</p>
                  </div>
                )}
              </div>

              <aside className="bg-white border border-border p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Seller Asking Price</div>
                <div className="font-display text-3xl text-ink mt-1">{price > 0 ? formatINR(price) : "-"}</div>

                <div className="mt-5 pt-5 border-t border-border">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Set by Tharlo
                  </div>
                  <div className="mt-2 text-sm">
                    <div><span className="text-muted-foreground">Fair Price Range: </span><span className="text-gold font-semibold">{price > 0 ? `${formatINR(fairMin)} – ${formatINR(fairMax)}` : "TBD"}</span></div>
                    <div className="mt-1"><span className="text-muted-foreground">Connectivity Score: </span><span className="text-muted-foreground italic">Assigned after review</span></div>
                    <div className="mt-1"><span className="text-muted-foreground">Legal Verification: </span><span className="text-muted-foreground italic">{form.legal_status || "Pending"}</span></div>
                    <div className="mt-1"><span className="text-muted-foreground">Appreciation Potential: </span><span className="text-muted-foreground italic">Assigned after review</span></div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-border">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Buyer Actions</div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Buyers will see: Request Callback form, WhatsApp, Call, Save, Compare. All leads route to Tharlo advisors — your contact stays private.
                  </div>
                </div>
              </aside>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-border p-4 flex flex-col sm:flex-row gap-3">
            <button onClick={onClose} data-testid="preview-edit" className="btn-outline flex-1">← Back to Edit</button>
            <button onClick={onConfirm} disabled={submitting} data-testid="preview-confirm-submit" className="btn-primary flex-1">
              {submitting ? "Publishing..." : "Looks Good — Publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecTile({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg text-ink">{value}</div>
    </div>
  );
}

function AmenityBadge({ label, yes }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 border ${yes ? "border-gold/40 bg-gold/5 text-ink" : "border-border bg-muted/30 text-muted-foreground line-through"}`}>
      <span className={`w-1.5 h-1.5 ${yes ? "bg-gold" : "bg-muted-foreground"}`} />
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

function BrokerHero() {
  return (
    <>
      <section className="relative bg-ink text-white py-20">
        <div className="container-tharlo grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-gold mb-3">For Brokers · Developers · Agencies</div>
            <h1 className="font-display text-4xl lg:text-6xl tracking-tight">Tharlo works <em className="text-gold not-italic">with brokers</em>, not against them.</h1>
            <p className="mt-5 text-white/80 max-w-md">Submit properties, track performance, and receive premium qualified leads. Brokers, developers and owners — all welcome on one professional platform.</p>
            <div className="mt-6 flex gap-3">
              <a href="#list-form" className="btn-gold">List a Property</a>
              <Link to="/broker" className="border border-white/40 text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-ink transition-colors">Go to Dashboard</Link>
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1681505531034-8d67054e07f6?w=900&q=85" alt="" className="w-full aspect-[4/3] object-cover" />
        </div>
      </section>

      <section className="container-tharlo py-14">
        <div className="overline mb-3">Why brokers choose Tharlo</div>
        <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">A platform built for serious brokers</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: <Users className="w-6 h-6" />, title: "Premium Leads", text: "Pre-qualified buyers with verified budgets and timelines." },
            { icon: <Award className="w-6 h-6" />, title: "Brand Visibility", text: "Get associated with a trusted, verified platform." },
            { icon: <Briefcase className="w-6 h-6" />, title: "Professional Tools", text: "Dashboard, listing management, lead tracking." },
            { icon: <BarChart3 className="w-6 h-6" />, title: "Analytics", text: "Locality reports, market trends, performance insights (coming soon)." },
            { icon: <TrendingUp className="w-6 h-6" />, title: "CRM", text: "Built-in CRM to manage clients and follow-ups (coming soon)." },
            { icon: <Building2 className="w-6 h-6" />, title: "Developer Network", text: "Direct relationships with verified developers and inventory." },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-border p-6">
              <div className="text-gold mb-3">{b.icon}</div>
              <h3 className="font-display text-lg text-ink">{b.title}</h3>
              <p className="text-foreground/70 text-sm mt-1">{b.text}</p>
            </div>
          ))}
        </div>
        <div id="list-form" className="mt-14 pt-6 border-t border-border">
          <div className="overline mb-2">Ready to list?</div>
          <h2 className="font-display text-3xl text-ink">Add your first property below</h2>
        </div>
      </section>
    </>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl text-ink flex items-center gap-2">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gold bg-gold/10 border border-gold/30 px-2 py-1">Public · Buyers See This</span>
      </div>
      {children}
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-white border border-border">
      <span className="text-sm text-ink font-medium">{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        <button type="button" data-testid={`${testid}-yes`} onClick={() => onChange(true)} className={`px-4 py-1.5 text-xs font-semibold border ${value === true ? "bg-ink text-white border-ink" : "border-border text-muted-foreground hover:border-ink"}`}>Yes</button>
        <button type="button" data-testid={`${testid}-no`} onClick={() => onChange(false)} className={`px-4 py-1.5 text-xs font-semibold border ${value === false ? "bg-ink text-white border-ink" : "border-border text-muted-foreground hover:border-ink"}`}>No</button>
      </div>
    </div>
  );
}
